import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Trophy, 
  Users, 
  BarChart3, 
  Clock, 
  Target, 
  Shield, 
  Smartphone, 
  Database,
  Zap,
  Globe
} from 'lucide-react';

const FeaturesPage = () => {
  const features = [
    {
      icon: <Trophy className="h-8 w-8 text-primary" />,
      title: "Tournament Management",
      description: "Create and manage cricket tournaments with multiple teams, matches, and comprehensive statistics tracking.",
      badge: "Core Feature"
    },
    {
      icon: <Users className="h-8 w-8 text-primary" />,
      title: "Team & Player Management",
      description: "Organize teams, manage player profiles, track performance history, and maintain detailed player statistics.",
      badge: "Core Feature"
    },
    {
      icon: <Clock className="h-8 w-8 text-primary" />,
      title: "Live Match Scoring",
      description: "Real-time match scoring with ball-by-ball updates, live scoreboards, and instant statistics calculation.",
      badge: "Live"
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-primary" />,
      title: "Advanced Analytics",
      description: "Comprehensive statistics and performance metrics with interactive charts and detailed reports.",
      badge: "Analytics"
    },
    {
      icon: <Target className="h-8 w-8 text-primary" />,
      title: "Performance Tracking",
      description: "Track individual and team performance over time with detailed batting, bowling, and fielding statistics.",
      badge: "Tracking"
    },
    {
      icon: <Shield className="h-8 w-8 text-primary" />,
      title: "Secure & Reliable",
      description: "Enterprise-grade security with data encryption, user authentication, and regular backups.",
      badge: "Security"
    },
    {
      icon: <Smartphone className="h-8 w-8 text-primary" />,
      title: "Mobile Responsive",
      description: "Fully responsive design that works seamlessly on desktop, tablet, and mobile devices.",
      badge: "Responsive"
    },
    {
      icon: <Database className="h-8 w-8 text-primary" />,
      title: "Data Management",
      description: "Efficient data storage and retrieval with support for large datasets and historical records.",
      badge: "Data"
    },
    {
      icon: <Zap className="h-8 w-8 text-primary" />,
      title: "Real-time Updates",
      description: "Instant updates across all connected devices with live synchronization and notifications.",
      badge: "Real-time"
    },
    {
      icon: <Globe className="h-8 w-8 text-primary" />,
      title: "Cloud-Based",
      description: "Access your data from anywhere with cloud storage and cross-platform compatibility.",
      badge: "Cloud"
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Features
          </h1>
          <p className="text-xl text-muted-foreground">
            Discover the powerful features that make our cricket scoring application the perfect choice for tournaments, teams, and cricket enthusiasts.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((feature, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    {feature.icon}
                    <div>
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {feature.badge}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-muted-foreground mb-6">
                Join thousands of cricket enthusiasts who trust our platform for their tournament and match management needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors">
                  Start Free Trial
                </button>
                <button className="border border-border px-6 py-3 rounded-lg font-semibold hover:bg-accent transition-colors">
                  Contact Sales
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FeaturesPage; 